import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { ShareProfileModal } from '../components/ShareProfileModal';
import { api, ApiError } from '../lib/api';
import { KYC_OPTIONS, kycTone, RISK_LABEL, RISK_OPTIONS, riskTone } from '../lib/crm';
import { amount, initials, tone } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/crm.css';
import '../styles/finance.css';

interface Contact { id: string; name: string; position: string | null; phone: string | null; mobile: string | null; email: string | null; emails: string[]; isPrimary: boolean }
interface CpBank { id: string; bankName: string; holder: string | null; iban: string | null; accountNumber: string | null; swift: string | null; currency: string; isPrimary: boolean }
interface Profile {
  id: string; name: string; kinds: string[]; kindLabels: string[];
  country: string | null; address: string | null; city: string | null; postalCode: string | null;
  taxNumber: string | null; registrationNumber: string | null; email: string | null; phone: string | null; website: string | null;
  currency: string; paymentTermsDays: number; creditLimitMinor: number | null;
  kycStatus: string; kycLabel: string; riskLevel: string; visibility: string; notes: string | null;
  contacts: Contact[]; bankAccounts: CpBank[];
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="meta__row">
      <span className="meta__k">{label}</span>
      <span className="meta__v">{value || '—'}</span>
    </div>
  );
}

export function CounterpartyDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { company } = useSession();
  const [p, setP] = useState<Profile | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Profile>>({});
  const [busy, setBusy] = useState(false);

  const [addingContact, setAddingContact] = useState(false);
  const [contact, setContact] = useState({ name: '', position: '', phone: '', mobile: '', emails: '' });
  const [sharing, setSharing] = useState(false);
  const [addingBank, setAddingBank] = useState(false);
  const [bank, setBank] = useState({ bankName: '', holder: '', iban: '', swift: '', currency: 'AED', isPrimary: false });

  const load = useCallback(() => {
    if (!id) return;
    api.get<Profile>(`/counterparties/${id}`).then((d) => { setP(d); setForm(d); }).catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo cargar.'));
  }, [id]);
  useEffect(load, [load, company]);

  const saveBase = async () => {
    setBusy(true);
    try {
      await api.patch(`/counterparties/${id}`, {
        address: form.address ?? undefined, city: form.city ?? undefined, postalCode: form.postalCode ?? undefined,
        country: form.country ?? undefined, taxNumber: form.taxNumber ?? undefined,
        email: form.email ?? undefined, phone: form.phone ?? undefined,
        currency: form.currency, paymentTermsDays: Number(form.paymentTermsDays) || 0,
        creditLimitMinor: form.creditLimitMinor ?? null, riskLevel: form.riskLevel, kycStatus: form.kycStatus,
        notes: form.notes ?? undefined,
      });
      setEditing(false); load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo guardar.'); }
    finally { setBusy(false); }
  };

  const addContact = async () => {
    if (contact.name.trim().length < 1) return;
    setBusy(true);
    try {
      await api.post(`/counterparties/${id}/contacts`, {
        name: contact.name.trim(), position: contact.position.trim() || undefined,
        phone: contact.phone.trim() || undefined, mobile: contact.mobile.trim() || undefined,
        emails: contact.emails.split(',').map((e) => e.trim()).filter(Boolean),
      });
      setContact({ name: '', position: '', phone: '', mobile: '', emails: '' }); setAddingContact(false); load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not add the contact.'); }
    finally { setBusy(false); }
  };
  const delContact = async (cid: string) => { await api.del(`/counterparties/${id}/contacts/${cid}`).catch(() => void 0); load(); };

  const addBank = async () => {
    if (bank.bankName.trim().length < 1) return;
    setBusy(true);
    try {
      await api.post(`/counterparties/${id}/bank-accounts`, {
        bankName: bank.bankName.trim(), holder: bank.holder.trim() || undefined,
        iban: bank.iban.trim() || undefined, swift: bank.swift.trim() || undefined,
        currency: bank.currency.trim() || 'AED', isPrimary: bank.isPrimary,
      });
      setBank({ bankName: '', holder: '', iban: '', swift: '', currency: 'AED', isPrimary: false }); setAddingBank(false); load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not add the account.'); }
    finally { setBusy(false); }
  };
  const delBank = async (bid: string) => { await api.del(`/counterparties/${id}/bank-accounts/${bid}`).catch(() => void 0); load(); };

  if (err && !p) return <main className="main"><button className="doc-back" onClick={() => nav('/relaciones')}><Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Relaciones</button><div className="card"><div className="empty">{err}</div></div></main>;
  if (!p) return <main className="main"><div className="faint" style={{ padding: 40 }}>Cargando ficha…</div></main>;

  const set = (k: keyof Profile) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  return (
    <main className="main main--wide">
      <button className="doc-back" onClick={() => nav('/relaciones')}><Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Relaciones</button>

      <div className="doc-head">
        <div className="cp-name" style={{ gap: 14 }}>
          <span className="cp-avatar" style={{ background: tone(p.id), width: 46, height: 46, fontSize: 17 }}>{initials(p.name)}</span>
          <div>
            <h1 className="h1" style={{ fontSize: 26 }}>{p.name}</h1>
            <div className="cp-kinds" style={{ marginTop: 6 }}>
              {p.kindLabels.map((k, i) => <span key={i} className="cp-kind">{k}</span>)}
              <span className={`pill pill--${kycTone(p.kycStatus)}`} style={{ marginLeft: 4 }}><span className="pill__dot" />{p.kycLabel}</span>
              <span className={`pill pill--${riskTone(p.riskLevel)}`}><span className="pill__dot" />{RISK_LABEL[p.riskLevel] ?? p.riskLevel}</span>
            </div>
          </div>
        </div>
        <div className="row gap-10">
          <button className="btn btn--ghost btn--sm" onClick={() => setSharing(true)}><Icon name="share" size={15} />Compartir / QR</button>
          {!editing && <button className="btn btn--primary btn--sm" onClick={() => setEditing(true)}><Icon name="sliders" size={15} />Editar datos</button>}
        </div>
      </div>

      {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}

      <div className="prof-grid" style={{ marginTop: 20 }}>
        {/* Columna izquierda: datos + cuentas */}
        <div className="col gap-20">
          <div className="card">
            <div className="card__head"><span className="card__title">Datos generales</span></div>
            <div className="card__body">
              {!editing ? (
                <>
                  <Field label="Address" value={p.address} />
                  <Field label="Ciudad" value={p.city} />
                  <Field label="Postal code" value={p.postalCode} />
                  <Field label="Country" value={p.country} />
                  <Field label="Nº fiscal (TRN)" value={p.taxNumber} />
                  <Field label="Email" value={p.email} />
                  <Field label="Phone" value={p.phone} />
                  <div className="meta__row"><span className="meta__k">Moneda</span><span className="meta__v num">{p.currency}</span></div>
                  <div className="meta__row"><span className="meta__k">Payment term</span><span className="meta__v num">{p.paymentTermsDays} days</span></div>
                  <div className="meta__row"><span className="meta__k">Credit limit</span><span className="meta__v num">{p.creditLimitMinor != null ? amount(p.creditLimitMinor, p.currency) : 'No limit'}</span></div>
                  {p.notes && <div style={{ marginTop: 10 }}><span className="meta__k">Notas</span><p style={{ fontSize: 13, marginTop: 4 }}>{p.notes}</p></div>}
                </>
              ) : (
                <>
                  <div className="field"><label>Address</label><input className="input" value={form.address ?? ''} onChange={set('address')} /></div>
                  <div className="row gap-12">
                    <div className="field grow"><label>Ciudad</label><input className="input" value={form.city ?? ''} onChange={set('city')} /></div>
                    <div className="field" style={{ width: 130 }}><label>Postal code</label><input className="input" value={form.postalCode ?? ''} onChange={set('postalCode')} /></div>
                    <div className="field" style={{ width: 90 }}><label>Country</label><input className="input" value={form.country ?? ''} onChange={set('country')} /></div>
                  </div>
                  <div className="row gap-12">
                    <div className="field grow"><label>Nº fiscal (TRN)</label><input className="input" value={form.taxNumber ?? ''} onChange={set('taxNumber')} /></div>
                    <div className="field" style={{ width: 100 }}><label>Moneda</label><input className="input" value={form.currency ?? ''} onChange={set('currency')} /></div>
                  </div>
                  <div className="row gap-12">
                    <div className="field grow"><label>Email</label><input className="input" value={form.email ?? ''} onChange={set('email')} /></div>
                    <div className="field grow"><label>Phone</label><input className="input" value={form.phone ?? ''} onChange={set('phone')} /></div>
                  </div>
                  <div className="row gap-12">
                    <div className="field" style={{ width: 130 }}><label>Term (days)</label><input className="input" type="number" value={String(form.paymentTermsDays ?? 30)} onChange={set('paymentTermsDays')} /></div>
                    <div className="field grow"><label>Riesgo</label><select className="select" value={form.riskLevel} onChange={set('riskLevel')}>{RISK_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}</select></div>
                    <div className="field grow"><label>KYC</label><select className="select" value={form.kycStatus} onChange={set('kycStatus')}>{KYC_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}</select></div>
                  </div>
                  <div className="field"><label>Notas</label><textarea className="textarea" value={form.notes ?? ''} onChange={set('notes')} rows={2} /></div>
                  <div className="row gap-10">
                    <button className="btn btn--primary btn--sm" onClick={saveBase} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
                    <button className="btn btn--ghost btn--sm" onClick={() => { setEditing(false); setForm(p); }}>Cancelar</button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Cuentas bancarias */}
          <div className="card">
            <div className="card__head">
              <span className="card__title">Cuentas bancarias {p.bankAccounts.length > 0 && <span className="faint">· {p.bankAccounts.length}</span>}</span>
              <button className="btn btn--ghost btn--sm" onClick={() => setAddingBank((v) => !v)}><Icon name="plus" size={14} />Add</button>
            </div>
            <div className="card__body">
              {p.bankAccounts.length === 0 && !addingBank && <div className="empty" style={{ padding: 14 }}>No accounts. Add one per currency.</div>}
              {p.bankAccounts.map((b) => (
                <div key={b.id} className="bank-card">
                  {b.isPrimary && <span className="bank-card__default">Principal</span>}
                  <div className="bank-card__top">
                    <div><div className="bank-card__name">{b.bankName}</div><div className="bank-card__holder">{b.holder ?? '—'}{b.swift ? ` · ${b.swift}` : ''}</div></div>
                    <div className="row center gap-8">
                      <span className="bank-card__cur">{b.currency}</span>
                      <button className="contact-card__del" style={{ position: 'static' }} onClick={() => delBank(b.id)}><Icon name="x" size={14} /></button>
                    </div>
                  </div>
                  <div className="bank-card__iban">{b.iban ?? b.accountNumber ?? '—'}</div>
                </div>
              ))}
              {addingBank && (
                <div className="mini-form">
                  <div className="row gap-12"><div className="field grow"><label>Banco</label><input className="input" value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} /></div><div className="field" style={{ width: 100 }}><label>Moneda</label><input className="input" value={bank.currency} onChange={(e) => setBank({ ...bank, currency: e.target.value })} /></div></div>
                  <div className="field"><label>Titular</label><input className="input" value={bank.holder} onChange={(e) => setBank({ ...bank, holder: e.target.value })} /></div>
                  <div className="row gap-12"><div className="field grow"><label>IBAN</label><input className="input" value={bank.iban} onChange={(e) => setBank({ ...bank, iban: e.target.value })} /></div><div className="field" style={{ width: 130 }}><label>SWIFT</label><input className="input" value={bank.swift} onChange={(e) => setBank({ ...bank, swift: e.target.value })} /></div></div>
                  <label className="row gap-8 center" style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={bank.isPrimary} onChange={(e) => setBank({ ...bank, isPrimary: e.target.checked })} /> Cuenta principal</label>
                  <div className="row gap-10" style={{ marginTop: 10 }}><button className="btn btn--primary btn--sm" onClick={addBank} disabled={busy}>Add account</button><button className="btn btn--ghost btn--sm" onClick={() => setAddingBank(false)}>Cancelar</button></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha: contactos */}
        <div className="card">
          <div className="card__head">
            <span className="card__title">Personas de contacto {p.contacts.length > 0 && <span className="faint">· {p.contacts.length}</span>}</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setAddingContact((v) => !v)}><Icon name="plus" size={14} />Add</button>
          </div>
          <div className="card__body">
            <p className="faint" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>This is internal profile data; it does not appear on invoicing.</p>
            {p.contacts.length === 0 && !addingContact && <div className="empty" style={{ padding: 14 }}>Sin contactos registrados.</div>}
            {p.contacts.map((c) => (
              <div key={c.id} className="contact-card">
                <button className="contact-card__del" onClick={() => delContact(c.id)}><Icon name="x" size={14} /></button>
                <div className="contact-card__name">{c.name} {c.isPrimary && <span className="primary-badge">principal</span>}</div>
                {c.position && <div className="contact-card__role">{c.position}</div>}
                {c.phone && <div className="contact-card__line"><span className="k">Tel.</span>{c.phone}</div>}
                {c.mobile && <div className="contact-card__line"><span className="k">Mobile</span>{c.mobile}</div>}
                {(c.emails.length > 0 || c.email) && (
                  <div className="contact-card__line" style={{ alignItems: 'flex-start' }}>
                    <span className="k" style={{ marginTop: 3 }}>Emails</span>
                    <span>{(c.emails.length ? c.emails : [c.email!]).map((e, i) => <span key={i} className="email-chip">{e}</span>)}</span>
                  </div>
                )}
              </div>
            ))}
            {addingContact && (
              <div className="mini-form">
                <div className="row gap-12"><div className="field grow"><label>Nombre</label><input className="input" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} /></div><div className="field grow"><label>Cargo</label><input className="input" value={contact.position} onChange={(e) => setContact({ ...contact, position: e.target.value })} /></div></div>
                <div className="row gap-12"><div className="field grow"><label>Phone</label><input className="input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></div><div className="field grow"><label>Mobile</label><input className="input" value={contact.mobile} onChange={(e) => setContact({ ...contact, mobile: e.target.value })} /></div></div>
                <div className="field"><label>Emails (separados por comas)</label><input className="input" value={contact.emails} onChange={(e) => setContact({ ...contact, emails: e.target.value })} placeholder="ana@empresa.com, pagos@empresa.com" /></div>
                <div className="row gap-10"><button className="btn btn--primary btn--sm" onClick={addContact} disabled={busy}>Add contact</button><button className="btn btn--ghost btn--sm" onClick={() => setAddingContact(false)}>Cancelar</button></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {sharing && <ShareProfileModal cpId={p.id} cpName={p.name} onClose={() => setSharing(false)} />}
    </main>
  );
}
